import { memo, useMemo, useState, useCallback } from 'react';
import {
  VOLTAGE_OPERATIONAL_MIN_V, VOLTAGE_OPERATIONAL_MAX_V,
  VOLTAGE_OPERATIONAL_MIN_PU, VOLTAGE_OPERATIONAL_MAX_PU,
  VOLTAGE_WARNING_LOW_V, VOLTAGE_WARNING_HIGH_V,
  VOLTAGE_WARNING_LOW_PU, VOLTAGE_WARNING_HIGH_PU,
} from '../utils/voltageConstants';
import { loadingSeverity } from '../utils/loadingConstants';
import { useZoneStatistics } from '../hooks/useZoneStatistics';
import { switchZone } from '../services/zoneSwitchService';
import { useDashboardStore, matchesZoneFilter } from '../store/dashboardStore';
import { ZoneSearch } from '../components/shared/ZoneSearch';
import type { ZoneStatisticsSummary } from '../types/zone';
import { CONNECTION_KIND_SHORT, type ConnectionKind } from '../types/network';
import { puToVolts, NOMINAL_VOLTAGE_LN_V } from '../utils/electricalConstants';
import InfoButton from '../components/monitoring/InfoButton';
import { parseUtcTimestamp } from '../utils/timeUtils';
import { dataAgeMs, freshnessOfAge, formatAge } from '../utils/dataFreshness';
import { useInternalMetricsEnabled } from '../store/featureStore';
import { useControlAreaStatusByZone } from '../hooks/useControlAreaStatus';
import type { ControlAreaStatus } from '../services/controlService';
import { fmt, fmtInt } from '../utils/numberFormat';
import {
  getHealthStatus, isArchiveZone, sortZoneSummaries, matchesZoneCardFilters,
  ZONE_HEALTH_ORDER, ZONE_HEALTH_LABELS, ZONE_SORT_OPTIONS, type ZoneHealth, type ZoneSortKey,
} from '../utils/zoneDashboard';

// ── Helpers ──────────────────────────────────────────────────────────────────

const healthLabels: Record<ZoneHealth, string> = ZONE_HEALTH_LABELS;
const healthColors: Record<string, string> = {
  green: 'bg-green-500', yellow: 'bg-yellow-500', red: 'bg-red-500', gray: 'bg-gray-400', archive: 'bg-orange-400',
};
const healthTextColors: Record<string, string> = {
  green: 'text-green-600 dark:text-green-400',
  yellow: 'text-yellow-600 dark:text-yellow-400',
  red: 'text-red-600 dark:text-red-400',
  gray: 'text-gray-500 dark:text-gray-400',
  archive: 'text-orange-600 dark:text-orange-300',
};

function formatTimestamp(ts: string | null): string {
  if (!ts) return '-';
  const d = parseUtcTimestamp(ts);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleString('de-DE', {
    hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit', year: 'numeric',
  });
}

function formatDateOnly(ts: string | null | undefined): string {
  if (!ts) return '-';
  const d = parseUtcTimestamp(ts);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// ── Voltage Band Component (styled like stress test) ────────────────────────

function VoltageBandBar({ minPu, maxPu, label }: { minPu: number | null | undefined; maxPu: number | null | undefined; label: string }) {
  const hasData = minPu != null && maxPu != null && minPu > 0 && maxPu > 0;

  // Scale in volts from 207 to 253 V (EN 50160 band around Un = 230 V)
  const toPosV = (volts: number) => Math.max(0, Math.min(100, ((volts - VOLTAGE_OPERATIONAL_MIN_V) / (VOLTAGE_OPERATIONAL_MAX_V - VOLTAGE_OPERATIONAL_MIN_V)) * 100));
  const toPos = (v: number) => toPosV(puToVolts(v));

  // Red outside the EN 50160 band, orange inside the warning margin, green otherwise.
  const getColor = (v: number) => {
    if (v < VOLTAGE_OPERATIONAL_MIN_PU || v > VOLTAGE_OPERATIONAL_MAX_PU) return 'text-red-500';
    if (v < VOLTAGE_WARNING_LOW_PU || v > VOLTAGE_WARNING_HIGH_PU) return 'text-orange-400';
    return 'text-green-400';
  };

  if (!hasData) {
    return (
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-gray-500 dark:text-primary-400 w-5">{label}</span>
        <span className="text-xs text-gray-400 dark:text-primary-500">-</span>
      </div>
    );
  }

  const minPos = toPos(minPu);
  const maxPos = toPos(maxPu);
  const rangeWidth = Math.max(1.5, maxPos - minPos);
  const outOfBand = minPu < VOLTAGE_OPERATIONAL_MIN_PU || maxPu > VOLTAGE_OPERATIONAL_MAX_PU;
  const nearLimit = minPu < VOLTAGE_WARNING_LOW_PU || maxPu > VOLTAGE_WARNING_HIGH_PU;
  const rangeBg = outOfBand ? 'bg-red-500' : nearLimit ? 'bg-orange-400' : 'bg-green-400';

  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] font-medium text-gray-500 dark:text-primary-400 w-5 shrink-0">{label}</span>
      <div className="relative h-2.5 flex-1 rounded-full bg-gray-200 dark:bg-primary-600">
        {/* normal zone: inside the warning margin of the band */}
        <div className="absolute h-full bg-green-100 dark:bg-green-900/20 rounded-full"
          style={{ left: `${toPosV(VOLTAGE_WARNING_LOW_V)}%`, width: `${toPosV(VOLTAGE_WARNING_HIGH_V) - toPosV(VOLTAGE_WARNING_LOW_V)}%` }} />
        {/* center tick */}
        <div className="absolute h-full w-px bg-gray-400 dark:bg-primary-400/60" style={{ left: `${toPosV(NOMINAL_VOLTAGE_LN_V)}%` }} />
        {/* range */}
        <div className={`absolute h-full rounded-full ${rangeBg} opacity-80`}
          style={{ left: `${minPos}%`, width: `${rangeWidth}%` }} />
      </div>
      <div className="flex gap-1.5 shrink-0">
        <span className={`text-[10px] font-semibold ${getColor(minPu)}`}>{fmt(puToVolts(minPu), 0)}</span>
        <span className="text-[10px] text-gray-400">-</span>
        <span className={`text-[10px] font-semibold ${getColor(maxPu)}`}>{fmt(puToVolts(maxPu), 0)} V</span>
      </div>
    </div>
  );
}

function VoltageSection({ title, s, prefix }: {
  title: string;
  s: ZoneStatisticsSummary;
  prefix: 'current' | '24h';
}) {
  const l1Min = prefix === 'current' ? s.currentMinVoltageL1Pu : s.minVoltageL1Pu24h;
  const l1Max = prefix === 'current' ? s.currentMaxVoltageL1Pu : s.maxVoltageL1Pu24h;
  const l2Min = prefix === 'current' ? s.currentMinVoltageL2Pu : s.minVoltageL2Pu24h;
  const l2Max = prefix === 'current' ? s.currentMaxVoltageL2Pu : s.maxVoltageL2Pu24h;
  const l3Min = prefix === 'current' ? s.currentMinVoltageL3Pu : s.minVoltageL3Pu24h;
  const l3Max = prefix === 'current' ? s.currentMaxVoltageL3Pu : s.maxVoltageL3Pu24h;
  const overallMin = prefix === 'current' ? s.currentMinVoltagePu : s.minVoltagePu24h;
  const overallMax = prefix === 'current' ? s.currentMaxVoltagePu : s.maxVoltagePu24h;

  const hasPerPhase = l1Min != null || l2Min != null || l3Min != null;

  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500 dark:text-primary-400 mb-1.5">{title}</p>
      <div className="space-y-1">
        {hasPerPhase ? (
          <>
            <VoltageBandBar minPu={l1Min} maxPu={l1Max} label="L1" />
            <VoltageBandBar minPu={l2Min} maxPu={l2Max} label="L2" />
            <VoltageBandBar minPu={l3Min} maxPu={l3Max} label="L3" />
          </>
        ) : (
          <VoltageBandBar minPu={overallMin} maxPu={overallMax} label="U" />
        )}
      </div>
    </div>
  );
}

// ── Loading Bar ─────────────────────────────────────────────────────────────

function LoadingBar({ value, label }: { value: number | null | undefined; label: string }) {
  const val = value ?? 0;
  const hasData = value != null;
  const severity = loadingSeverity(val);
  const color = severity === 'critical' ? 'bg-red-500' : severity === 'warning' ? 'bg-orange-400' : 'bg-green-400';
  const textColor = severity === 'critical' ? 'text-red-600 dark:text-red-400' : severity === 'warning' ? 'text-orange-600 dark:text-orange-400' : 'text-gray-700 dark:text-primary-200';

  return (
    <div>
      <div className="flex justify-between items-baseline mb-0.5">
        <span className="text-[10px] text-gray-500 dark:text-primary-400">{label}</span>
        <span className={`text-xs font-bold ${hasData ? textColor : 'text-gray-400 dark:text-primary-500'}`}>
          {hasData ? `${fmt(val, 1)}%` : '-'}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-gray-200 dark:bg-primary-600">
        {hasData && <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${Math.min(val, 100)}%` }} />}
      </div>
    </div>
  );
}

// ── Zone Card ────────────────────────────────────────────────────────────────

// memo + zoneId-based callbacks: the 60 s statistics refresh replaces the whole list, and
// without memo all 148 cards re-rendered; with inline closures memo would never hit.
/**
 * § 14a readiness of the zone's Netzbereich (STEUERUNG.md 7.2 Nr. 17): enough minute values for network-oriented
 * control after the FNN recommendation 2e, only preventive control, or none; a running intervention wins.
 */
function Control14aRow({ row }: { row: ControlAreaStatus }) {
  const share = row.nzeSharePct;
  const required = row.nzeRequiredPct;
  const figures = share != null && required != null ? ` · Messquote ${fmt(share, 1)} % von nötig ${fmt(required, 0)} %` : '';
  const [label, tone] = row.controlled
    ? [`Steuerung läuft${row.kind === 'preventive' ? ' (präventiv)' : ''}`, 'bg-violet-50 dark:bg-violet-900/30 text-violet-800 dark:text-violet-200 border-violet-200 dark:border-violet-800']
    : row.nzeStatus === 'netzorientiert'
      ? ['netzorientiert steuerbar', 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300 border-green-200 dark:border-green-800']
      : row.nzeStatus === 'praeventiv_moeglich'
        ? ['Messung nicht ausreichend – nur präventiv', 'bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800']
        : row.nzeStatus === 'keine_steuerung'
          ? ['Messung nicht ausreichend – keine Steuerung', 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300 border-red-200 dark:border-red-800']
          : ['noch nicht bewertet', 'bg-gray-50 dark:bg-primary-900/40 text-gray-600 dark:text-primary-300 border-gray-200 dark:border-primary-700'];
  return (
    <div className="px-5 pb-3">
      <p className={`rounded-lg border px-2.5 py-1.5 text-[11px] ${tone}`} title={row.nzeText ?? undefined}>
        <span className="font-semibold">§ 14a:</span> {label}{figures}
      </p>
    </div>
  );
}

const ZoneCard = memo(function ZoneCard({ stats, control14a, onSelect, onOpenMonitoring, onToggleFavorite, isFavorite, isLoading }: {
  stats: ZoneStatisticsSummary;
  control14a?: ControlAreaStatus;
  onSelect: (zoneId: string) => void;
  onOpenMonitoring: (zoneId: string) => void;
  onToggleFavorite: (zoneId: string) => void;
  isFavorite: boolean;
  isLoading: boolean;
}) {
  const health = getHealthStatus(stats);
  const internalMetricsEnabled = useInternalMetricsEnabled();
  const zoneId = stats.zoneId;

  return (
    <div
      onClick={isLoading ? undefined : () => onSelect(zoneId)}
      className={`bg-white dark:bg-primary-800 rounded-xl border-2 border-gray-200 dark:border-primary-600 shadow-sm
        hover:shadow-lg hover:border-accent-primary dark:hover:border-accent-primary hover:-translate-y-0.5
        transition-all duration-200 cursor-pointer flex flex-col ${isLoading ? 'opacity-60 pointer-events-none' : ''}`}
    >
      {/* Header — zone identity colour as a chip (a full-width coloured border reads as a status) */}
      <div className="rounded-t-xl px-5 pt-4 pb-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-900 dark:text-primary-100 flex items-center gap-2 min-w-0">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onToggleFavorite(zoneId); }}
              className={`shrink-0 -ml-1 p-0.5 rounded transition-colors ${
                isFavorite
                  ? 'text-amber-500 hover:text-amber-600 dark:text-amber-400 dark:hover:text-amber-300'
                  : 'text-gray-300 hover:text-amber-500 dark:text-primary-500 dark:hover:text-amber-400'
              }`}
              title={isFavorite ? 'Aus Favoriten entfernen' : 'Als Favorit oben anheften'}
              aria-label={isFavorite ? 'Aus Favoriten entfernen' : 'Als Favorit oben anheften'}
              aria-pressed={isFavorite}
            >
              <StarIcon filled={isFavorite} />
            </button>
            <span
              className="w-2.5 h-2.5 rounded-full shrink-0 ring-1 ring-black/10 dark:ring-white/20"
              style={{ backgroundColor: stats.zoneColor }}
              title="Zonenfarbe auf der Karte"
            />
            <span className="truncate">{stats.zoneName}</span>
          </h3>
          <div className="flex items-center gap-2">
            {(stats.verbundZoneCount ?? 1) > 1 && (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 border border-violet-200 dark:border-violet-800"
                title={`Rechnet im Netzverbund mit ${(stats.verbundZoneCount ?? 1) - 1} weiteren Zonen · Trennstellen: ${stats.couplingsClosed ?? 0} geschlossen, ${stats.couplingsOpen ?? 0} offen`}
              >
                ⇄ Verbund · {stats.verbundZoneCount} Zonen
              </span>
            )}
            {(stats.verbundZoneCount ?? 1) <= 1 && (stats.couplingsOpen ?? 0) > 0 && (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-800"
                title={`${stats.couplingsOpen} offene Trennstellen zu Nachbarzonen`}
              >
                {stats.couplingsOpen} Trennst. offen
              </span>
            )}
            <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium ${healthTextColors[health]}`}>
              <span className={`w-2 h-2 rounded-full ${healthColors[health]}`} />
              {healthLabels[health]}
            </span>
          </div>
        </div>
      </div>

      {/* Element Counts Grid */}
      <div className="px-5 pb-3">
        <div className="grid grid-cols-3 gap-3">
          <CountTile icon="trafo" label="Trafos" value={stats.transformerCount} />
          <CountTile icon="house" label="Hausanschl." value={stats.houseConnectionCount} hint={connectionKindHint(stats.houseConnectionKinds)} />
          <CountTile icon="cable" label="Kabel" value={stats.cableCount} />
          {/* Percentage only while it is a meaningful coverage figure — with
              several meters per house (Mehrfamilienhäuser) SMGWs can exceed
              the house count and "270%" reads as a bug, not a ratio. */}
          <CountTile icon="smgw" label="SMGWs" value={stats.smgwActiveCount} suffix={stats.houseConnectionCount > 0 && stats.smgwActiveCount <= stats.houseConnectionCount ? `(${Math.round(stats.smgwActiveCount / stats.houseConnectionCount * 100)}%)` : undefined} />
          <CountTile icon="controlbox" label="Steuerboxen" value={stats.controlBoxCount} />
          <CountTile icon="sum" label="Gesamt" value={stats.transformerCount + stats.houseConnectionCount + stats.cableCount} />
        </div>
      </div>

      {/* Technical Resources */}
      <div className="px-5 pb-3">
        <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500 dark:text-primary-400 mb-1.5">Technische Ressourcen</p>
        <div className="grid grid-cols-5 gap-2">
          <TrBadge label="PV" count={stats.pvCount} icon="pv" color="bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800" />
          <TrBadge label="Wallbox" count={stats.wallboxCount} icon="wallbox" color="bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-800" />
          <TrBadge label="WP" count={stats.heatPumpCount} icon="wp" color="bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800" />
          <TrBadge label="Batterie" count={stats.batteryCount} icon="battery" color="bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800" />
          <TrBadge label="Klima" count={stats.airConditioningCount} icon="klima" color="bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-300 border-cyan-200 dark:border-cyan-800" />
        </div>
      </div>

      {control14a && <Control14aRow row={control14a} />}

      {/* Divider */}
      <div className="border-t border-gray-100 dark:border-primary-700 mx-5" />

      {/* Archive zones: extent + archive-wide extremes instead of live/24h */}
      {isArchiveZone(stats) ? (
        <div className="px-5 py-3 flex-1 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500 dark:text-primary-400">Lastfluss-Archiv</p>
            <span className="text-[10px] font-medium text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-full px-2 py-0.5">
              {formatDateOnly(stats.archiveFrom)} – {formatDateOnly(stats.archiveTo)}
            </span>
          </div>
          <p className="text-[11px] text-gray-500 dark:text-primary-400">
            {fmtInt(stats.archiveSnapshotCount ?? 0)} gespeicherte Lastfluss-Zeitpunkte (15-Minuten-Raster). Kennzahlen unten sind Extremwerte über den gesamten Zeitraum.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2.5">
              <LoadingBar value={stats.archiveMaxCableLoadingPercent ?? null} label="Max. Kabelauslastung" />
              <LoadingBar value={stats.archiveMaxTrafoLoadingPercent ?? null} label="Max. Trafoauslastung" />
            </div>
            <div className="space-y-1">
              <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500 dark:text-primary-400 mb-1.5">Spannungsband (L-N)</p>
              <VoltageBandBar minPu={stats.archiveMinVoltagePu} maxPu={stats.archiveMaxVoltagePu} label="U" />
            </div>
          </div>
        </div>
      ) : (
      <div className="px-5 py-3 grid grid-cols-2 gap-4 flex-1">
        {/* Left: Current */}
        <div className="space-y-2.5">
          <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500 dark:text-primary-400">Aktuell</p>
          <LoadingBar value={stats.currentMaxCableLoadingPercent} label="Max. Kabelauslastung" />
          <LoadingBar value={stats.currentMaxTrafoLoadingPercent} label="Max. Trafoauslastung" />
          <VoltageSection title="Spannungsband (L-N)" s={stats} prefix="current" />
        </div>

        {/* Right: 24h */}
        <div className="space-y-2.5">
          <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500 dark:text-primary-400">Letzte 24h</p>
          <LoadingBar value={stats.maxCableLoadingPercent24h} label="Max. Kabelauslastung" />
          <LoadingBar value={stats.maxTrafoLoadingPercent24h} label="Max. Trafoauslastung" />
          <VoltageSection title="Spannungsband (L-N)" s={stats} prefix="24h" />

          {/* Alert count badges — clickable, opens monitoring */}
          {((stats.trafoAlertCount24h ?? 0) + (stats.voltageAlertCount24h ?? 0) + (stats.cableAlertCount24h ?? 0)) > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); onOpenMonitoring(zoneId); }}
              className="w-full mt-1 p-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors cursor-pointer text-left group"
              title="Monitoring-Seite mit Alarm-Details öffnen"
            >
              <p className="text-[10px] uppercase tracking-wider font-bold text-amber-600 dark:text-amber-400 mb-1.5 flex items-center justify-between">
                <span>Alarme (24h)</span>
                <svg className="w-3 h-3 opacity-50 group-hover:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </p>
              <div className="flex items-center gap-1.5 flex-wrap">
                {(stats.trafoAlertCount24h ?? 0) > 0 && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                    &#9889; {stats.trafoAlertCount24h} Trafo
                  </span>
                )}
                {(stats.cableAlertCount24h ?? 0) > 0 && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                    &#9866; {stats.cableAlertCount24h} Kabel
                  </span>
                )}
                {(stats.voltageAlertCount24h ?? 0) > 0 && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                    &#9889; {stats.voltageAlertCount24h} U
                  </span>
                )}
              </div>
            </button>
          )}
        </div>
      </div>
      )}

      {/* Model quality trust indicator — internal diagnostic, dev deployments only */}
      {internalMetricsEnabled && stats.avgModelDeviationPercent != null && (() => {
        const accuracy = 100 - stats.avgModelDeviationPercent;
        const qualityLabel = accuracy >= 95 ? 'Gut' : accuracy >= 90 ? 'Eingeschränkt' : 'Unzuverlässig';
        const colorClasses = accuracy >= 95
          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
          : accuracy >= 90
          ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
          : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
        const outletCount = stats.comparisonOutletCount ?? 0;
        const maxDev = stats.maxModelDeviationPercent;
        return (
          <div
            className="px-5 py-2 border-t border-gray-100 dark:border-primary-700"
            title={`Übereinstimmung zwischen Lastfluss-Berechnung und Lemonbeat-Messung an ${outletCount} Abgangsleitungen (Wirkleistung P)`}
          >
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-gray-500 dark:text-primary-400 flex items-center gap-1">
                Modellqualität
                <InfoButton
                  size="small"
                  position="below"
                  text="Vergleicht die berechnete Wirkleistung (P) an jeder Abgangsleitung mit der tatsächlichen Lemonbeat-Messung. Der Prozentwert zeigt, wie genau das Lastfluss-Modell die reale Leistungsverteilung im Netz abbildet. ≥95% = Gut (Belastungswerte vertrauenswürdig), ≥90% = Eingeschränkt (Richtung stimmt, Werte ungenau), <90% = Unzuverlässig (Ergebnisse nicht als Entscheidungsgrundlage nutzen)."
                />
              </span>
              <button
                onClick={(e) => { e.stopPropagation(); onOpenMonitoring(zoneId); }}
                className={`text-xs font-bold px-2 py-0.5 rounded-full cursor-pointer hover:opacity-80 transition-opacity ${colorClasses}`}
              >
                {fmt(accuracy, 1)}% &middot; {qualityLabel}
              </button>
            </div>
            <div className="flex items-center justify-between mt-0.5">
              {outletCount > 0 && (
                <span className="text-[10px] text-gray-400 dark:text-primary-500">
                  {outletCount} Messpunkte
                </span>
              )}
              {maxDev != null && (
                <span className="text-[10px] text-gray-400 dark:text-primary-500">
                  max. {fmt(maxDev, 1)}% Abw.
                </span>
              )}
            </div>
          </div>
        );
      })()}

      {/* Lemonbeat staleness warning */}
      {(() => {
        const ageMs = dataAgeMs(stats.lastMeasurementAt);
        const freshness = freshnessOfAge(ageMs);
        if (ageMs == null || freshness === 'fresh' || freshness === 'none') return null;
        const isHard = freshness === 'unavailable';
        return (
          <div className={`px-5 py-1.5 border-t flex items-center gap-1.5 text-[10px] ${
            isHard
              ? 'border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400'
              : 'border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400'
          }`}>
            <svg className="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
            </svg>
            <span>Lemonbeat {isHard ? 'nicht verfügbar' : 'verzögert'} seit {formatAge(ageMs)}</span>
          </div>
        );
      })()}

      {/* Footer */}
      <div className="px-5 py-2 bg-gray-50 dark:bg-primary-900/50 rounded-b-xl flex items-center justify-between">
        <span className="text-[10px] text-gray-400 dark:text-primary-500">
          {isArchiveZone(stats)
            ? `Archivende: ${formatTimestamp(stats.archiveTo ?? null)} Uhr`
            : `Aktualisiert: ${formatTimestamp(stats.lastPowerFlowTimestamp ?? stats.lastSnapshotTimestamp)} Uhr`}
        </span>
        <span className="text-[10px] font-medium text-accent-primary">
          Netzgebiet öffnen →
        </span>
      </div>
    </div>
  );
});

function FilterChip({ active, onClick, title, children }: { active: boolean; onClick: () => void; title: string; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-pressed={active}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium transition-colors ${
        active
          ? 'bg-accent-primary/10 border-accent-primary text-accent-primary dark:bg-accent-primary/20'
          : 'bg-white dark:bg-primary-700 border-gray-300 dark:border-primary-500 text-gray-600 dark:text-primary-200 hover:border-accent-primary hover:text-accent-primary'
      }`}
    >
      {children}
    </button>
  );
}

function StarIcon({ filled }: { filled: boolean }) {
  return (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.5a.56.56 0 0 1 1.04 0l2.13 5.11a.56.56 0 0 0 .47.35l5.52.44c.5.04.7.66.32.99l-4.2 3.6a.56.56 0 0 0-.18.56l1.28 5.38a.56.56 0 0 1-.84.61l-4.72-2.88a.56.56 0 0 0-.59 0l-4.72 2.88a.56.56 0 0 1-.84-.61l1.28-5.38a.56.56 0 0 0-.18-.56l-4.2-3.6a.56.56 0 0 1 .32-.99l5.52-.44a.56.56 0 0 0 .47-.35L11.48 3.5Z" />
    </svg>
  );
}

/** Shared 16px stroke icon set — keeps the tiles visually uniform (no mixed emoji rendering). */
function TileIcon({ name, className }: { name: string; className?: string }) {
  const paths: Record<string, React.ReactNode> = {
    trafo: <path strokeLinecap="round" strokeLinejoin="round" d="M13 2 4.5 13.5H11L9.5 22 18 10.5h-6.5L13 2Z" />,
    house: <path strokeLinecap="round" strokeLinejoin="round" d="m3 11 9-8 9 8M5 9.5V21h14V9.5" />,
    cable: <path strokeLinecap="round" strokeLinejoin="round" d="M3 17c4 0 5-10 9-10s5 10 9 10" />,
    smgw: <path strokeLinecap="round" strokeLinejoin="round" d="M12 21v-8m0 0a3 3 0 1 0-.01 0M5.6 5.6a9 9 0 0 0 0 12.8M18.4 5.6a9 9 0 0 1 0 12.8M8.1 8.1a5.5 5.5 0 0 0 0 7.8M15.9 8.1a5.5 5.5 0 0 1 0 7.8" />,
    controlbox: <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16M9 4v4m6 4v4m-9 4v.01" />,
    sum: <path strokeLinecap="round" strokeLinejoin="round" d="M18 5H6l6 7-6 7h12" />,
    pv: <path strokeLinecap="round" strokeLinejoin="round" d="M12 5V3m0 18v-2m7-7h2M3 12h2m11.95-4.95 1.41-1.41M5.64 18.36l1.41-1.41m0-9.9L5.64 5.64m12.72 12.72-1.41-1.41M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z" />,
    wallbox: <path strokeLinecap="round" strokeLinejoin="round" d="M9 3v5m6-5v5M7 8h10v4a5 5 0 0 1-10 0V8Zm5 9v4" />,
    wp: <path strokeLinecap="round" strokeLinejoin="round" d="M14 14.76V5a2 2 0 1 0-4 0v9.76a4.5 4.5 0 1 0 4 0ZM12 10v7" />,
    battery: <path strokeLinecap="round" strokeLinejoin="round" d="M9 2h6M7 5h10a1 1 0 0 1 1 1v15a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Zm5 4-2.5 5H13l-2.5 5" />,
    klima: <path strokeLinecap="round" strokeLinejoin="round" d="M12 2v20M4 6l16 12M20 6 4 18M12 2l-2 2m2-2 2 2m-2 16-2-2m2 2 2-2M4 6l2.7.5M4 6l.5 2.7M20 18l-2.7-.5m2.2 3.2L19 18M20 6l-2.7.5M19.5 3.3 20 6M4 18l2.7-.5M3.5 20.7 4 18" />,
  };
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className={className ?? 'w-4 h-4'} aria-hidden="true">
      {paths[name] ?? <circle cx="12" cy="12" r="4" />}
    </svg>
  );
}

function CountTile({ icon, label, value, suffix, hint }: { icon: string; label: string; value: number; suffix?: string; hint?: string }) {
  return (
    <div className="bg-gray-50 dark:bg-primary-700/50 rounded-lg px-2.5 py-1.5 text-center" title={hint}>
      <TileIcon name={icon} className="w-4 h-4 mx-auto text-gray-400 dark:text-primary-400" />
      <p className="text-base font-bold text-gray-900 dark:text-primary-100 leading-tight">
        {value}
        {suffix && <span className="text-[11px] font-medium text-gray-400 dark:text-primary-400 ml-0.5">{suffix}</span>}
      </p>
      <p className="text-[11px] text-gray-500 dark:text-primary-400 leading-tight">{label}</p>
      {hint && <p className="text-[10px] text-gray-400 dark:text-primary-500 leading-tight truncate">{hint}</p>}
    </div>
  );
}

/** "12 Gewerbe · 3 RLM · 1 Säule" — the non-household part of the house connections, empty when all are households. */
function connectionKindHint(kinds: ZoneStatisticsSummary['houseConnectionKinds']): string | undefined {
  if (!kinds) return undefined;
  const parts = (Object.keys(kinds) as ConnectionKind[])
    .filter(k => (kinds[k] ?? 0) > 0)
    .map(k => `${kinds[k]} ${CONNECTION_KIND_SHORT[k]}`);
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

function TrBadge({ label, count, icon, color }: { label: string; count: number; icon: string; color: string }) {
  return (
    <div className={`rounded-lg border px-2 py-1.5 text-center ${color}`}>
      <TileIcon name={icon} className="w-4 h-4 mx-auto" />
      <p className="text-sm font-bold leading-tight">{count}</p>
      <p className="text-[11px] leading-tight">{label}</p>
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────────────────────

export default function LandingPage() {
  const { statistics, isLoading, error, reload: loadStatistics } = useZoneStatistics();
  const control14aByZone = useControlAreaStatusByZone();
  const [isSwitching, setIsSwitching] = useState(false);
  const [visibleCount, setVisibleCount] = useState(30);
  const zoneFilter = useDashboardStore(s => s.zoneFilter);
  const sortKey = useDashboardStore(s => s.sortKey);
  const setSortKey = useDashboardStore(s => s.setSortKey);
  const statusFilter = useDashboardStore(s => s.statusFilter);
  const toggleStatus = useDashboardStore(s => s.toggleStatus);
  const onlyFavorites = useDashboardStore(s => s.onlyFavorites);
  const setOnlyFavorites = useDashboardStore(s => s.setOnlyFavorites);
  const onlyWithAlerts = useDashboardStore(s => s.onlyWithAlerts);
  const setOnlyWithAlerts = useDashboardStore(s => s.setOnlyWithAlerts);
  const clearFilters = useDashboardStore(s => s.clearFilters);
  const favoriteZoneIds = useDashboardStore(s => s.favoriteZoneIds);
  const toggleFavorite = useDashboardStore(s => s.toggleFavorite);

  const favoriteSet = useMemo(() => new Set(favoriteZoneIds), [favoriteZoneIds]);
  const isSearched = zoneFilter.trim().length > 0;
  const isFiltered = isSearched || statusFilter.length > 0 || onlyFavorites || onlyWithAlerts;

  // Counts per status over the searched set, so the chips tell the operator how many
  // "Kritisch"/"Warnung" zones exist before they narrow the list down further.
  const searchedStats = useMemo(
    () => statistics.filter(s => matchesZoneFilter({ name: s.zoneName, id: s.zoneId }, zoneFilter)),
    [statistics, zoneFilter]
  );
  const statusCounts = useMemo(() => {
    const counts: Record<ZoneHealth, number> = { red: 0, yellow: 0, green: 0, gray: 0, archive: 0 };
    for (const s of searchedStats) counts[getHealthStatus(s)]++;
    return counts;
  }, [searchedStats]);
  const visibleStats = useMemo(() => {
    const filters = { statuses: new Set(statusFilter), onlyFavorites, onlyWithAlerts };
    const filtered = searchedStats.filter(s => matchesZoneCardFilters(s, filters, favoriteSet));
    return sortZoneSummaries(filtered, sortKey, favoriteSet);
  }, [searchedStats, statusFilter, onlyFavorites, onlyWithAlerts, favoriteSet, sortKey]);

  const handleZoneSelect = useCallback(async (zoneId: string) => {
    setIsSwitching(true);
    try {
      await switchZone(zoneId, { tab: 'map', waitForMapResize: true });
    } catch (err) {
      console.error('[LandingPage] Zone switch failed:', err);
    } finally {
      setIsSwitching(false);
    }
  }, []);

  const handleOpenMonitoring = useCallback(async (zoneId: string) => {
    setIsSwitching(true);
    try {
      await switchZone(zoneId, { tab: 'monitoring' });
    } finally {
      setIsSwitching(false);
    }
  }, []);

  if (isLoading && statistics.length === 0) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50 dark:bg-primary-900">
        <div className="flex items-center gap-3 text-gray-500 dark:text-primary-400">
          <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Lade Netzgebiete...
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-gray-50 dark:bg-primary-900 p-6">
      <div className="max-w-[1800px] mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-end justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-primary-100">Netzgebiete</h1>
            <p className="text-sm text-gray-500 dark:text-primary-400 mt-0.5">
              {isFiltered
                ? <>{visibleStats.length} von {statistics.length} Zone{statistics.length !== 1 ? 'n' : ''}
                    {isSearched ? <> passen zu „{zoneFilter.trim()}"</> : ' entsprechen dem Filter'}{' '}
                    <button onClick={clearFilters} className="text-accent-primary hover:underline">Filter zurücksetzen</button></>
                : <>{statistics.length} Zone{statistics.length !== 1 ? 'n' : ''} verfügbar</>}
            </p>
          </div>
          <button
            onClick={loadStatistics}
            disabled={isLoading}
            className="text-xs text-gray-500 dark:text-primary-400 hover:text-accent-primary transition-colors flex items-center gap-1.5"
          >
            <svg className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Aktualisieren
          </button>
        </div>

        {/* Sort + filter toolbar — with 148 zones the operator needs "their" zones first */}
        {statistics.length > 1 && (
          <div className="mb-5 flex flex-wrap items-center gap-x-4 gap-y-2">
            <ZoneSearch className="w-full sm:w-80" />

            <label className="flex items-center gap-2 text-xs text-gray-500 dark:text-primary-400">
              <span>Sortierung</span>
              <select
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value as ZoneSortKey)}
                className="px-2 py-[5px] bg-white dark:bg-primary-700 border border-gray-300 dark:border-primary-500 text-gray-900 dark:text-primary-100 rounded focus:outline-hidden focus:ring-1 focus:ring-accent-primary text-xs"
                aria-label="Zonen sortieren nach"
              >
                {ZONE_SORT_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
              </select>
            </label>

            <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Nach Status filtern">
              {ZONE_HEALTH_ORDER.filter(h => statusCounts[h] > 0 || statusFilter.includes(h)).map(h => (
                <FilterChip
                  key={h}
                  active={statusFilter.includes(h)}
                  onClick={() => toggleStatus(h)}
                  title={statusFilter.includes(h) ? `Status „${healthLabels[h]}" nicht mehr filtern` : `Nur Zonen mit Status „${healthLabels[h]}" zeigen`}
                >
                  <span className={`w-2 h-2 rounded-full ${healthColors[h]}`} />
                  {healthLabels[h]}
                  <span className="opacity-60">{statusCounts[h]}</span>
                </FilterChip>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-1.5">
              <FilterChip
                active={onlyFavorites}
                onClick={() => setOnlyFavorites(!onlyFavorites)}
                title="Nur Favoriten zeigen (Stern an der Zonenkarte)"
              >
                <StarIcon filled={onlyFavorites} />
                Favoriten
                {favoriteZoneIds.length > 0 && <span className="opacity-60">{favoriteZoneIds.length}</span>}
              </FilterChip>
              <FilterChip
                active={onlyWithAlerts}
                onClick={() => setOnlyWithAlerts(!onlyWithAlerts)}
                title="Nur Zonen mit Alarmen in den letzten 24 h"
              >
                Mit Alarmen (24h)
              </FilterChip>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300 flex items-center gap-2">
            <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            {error}
            <button onClick={loadStatistics} className="ml-auto underline font-medium">Erneut versuchen</button>
          </div>
        )}

        {visibleStats.length > 0 ? (
          <>
          <div className={`grid gap-5 ${
            visibleStats.length === 1 ? 'grid-cols-1 max-w-3xl' :
            visibleStats.length === 2 ? 'grid-cols-1 lg:grid-cols-2' :
            'grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3'
          }`}>
            {visibleStats.slice(0, visibleCount).map(s => (
              <ZoneCard
                key={s.zoneId}
                stats={s}
                control14a={control14aByZone.get(s.zoneId)}
                onSelect={handleZoneSelect}
                onOpenMonitoring={handleOpenMonitoring}
                onToggleFavorite={toggleFavorite}
                isFavorite={favoriteSet.has(s.zoneId)}
                isLoading={isSwitching}
              />
            ))}
          </div>
          {visibleCount < visibleStats.length && (
            <div className="mt-4 text-center">
              <button
                onClick={() => setVisibleCount(c => Math.min(c + 30, visibleStats.length))}
                className="px-4 py-2 text-sm text-accent-primary hover:text-accent-hover dark:text-accent-primary dark:hover:text-accent-hover font-medium transition-colors"
              >
                Weitere Zonen laden ({visibleStats.length - visibleCount} verbleibend)
              </button>
            </div>
          )}
          </>
        ) : isFiltered ? (
          <div className="text-center py-20">
            <p className="text-gray-500 dark:text-primary-400 text-lg">
              {isSearched ? <>Keine Zone passt zu „{zoneFilter.trim()}"</> : 'Keine Zone entspricht dem Filter'}
            </p>
            <button onClick={clearFilters} className="mt-2 text-sm text-accent-primary hover:underline">Filter zurücksetzen</button>
          </div>
        ) : (
          <div className="text-center py-20">
            <svg className="w-20 h-20 mx-auto mb-4 text-gray-300 dark:text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
            <p className="text-gray-500 dark:text-primary-400 text-lg">Keine Netzgebiete vorhanden</p>
            <p className="text-gray-400 dark:text-primary-500 text-sm mt-1">Erstellen Sie eine Zone in den Einstellungen</p>
          </div>
        )}

        {/* Scale legend for voltage bars */}
        {statistics.length > 0 && (
          <div className="mt-6 flex items-center justify-center gap-6 text-[10px] text-gray-400 dark:text-primary-500">
            <span>Spannungsband-Skala: {fmt(VOLTAGE_OPERATIONAL_MIN_V, 0)} V — {fmt(NOMINAL_VOLTAGE_LN_V, 0)} V (Un) — {fmt(VOLTAGE_OPERATIONAL_MAX_V, 0)} V</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400" /> ±5 % ok</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-400" /> ±5–10 %</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> &gt; ±10 %</span>
          </div>
        )}
      </div>
    </div>
  );
}
